/// <reference types="google.maps" />
import { env } from '@config/env'

/**
 * Google Maps API script loading status
 */
let isScriptLoading = false
let isScriptLoaded = false
let loadPromise: Promise<void> | null = null

/**
 * Google Maps API types
 */
declare global {
  interface Window {
    google?: typeof google
    initGoogleMaps?: () => void
  }
}

/**
 * Load Google Maps API script dynamically
 * @returns Promise that resolves when script is loaded
 */
export function loadGoogleMapsScript(): Promise<void> {
  // If already loaded, return resolved promise
  if (isScriptLoaded && window.google?.maps) {
    return Promise.resolve()
  }

  // If currently loading, return existing promise
  if (isScriptLoading && loadPromise) {
    return loadPromise
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector('script.googleMapClass')
  if (existingScript && window.google?.maps) {
    isScriptLoaded = true
    return Promise.resolve()
  }

  // Start loading
  isScriptLoading = true
  loadPromise = new Promise((resolve, reject) => {
    // Remove existing script if present but not loaded
    if (existingScript) {
      existingScript.remove()
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.className = 'googleMapClass'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${env.googleApiKey}&libraries=places,geometry`
    script.async = true
    script.defer = true

    script.onload = () => {
      // Google Maps API may not set window.google immediately; wait for it
      const waitForGoogle = (attempts = 0) => {
        if (window.google?.maps) {
          isScriptLoading = false
          isScriptLoaded = true
          resolve()
          return
        }
        if (attempts < 100) {
          setTimeout(() => waitForGoogle(attempts + 1), 50)
        } else {
          isScriptLoading = false
          resolve()
        }
      }
      waitForGoogle()
    }

    script.onerror = () => {
      isScriptLoading = false
      loadPromise = null
      reject(new Error('Failed to load Google Maps API'))
    }

    document.head.appendChild(script)
  })

  return loadPromise
}

/**
 * Remove Google Maps script from DOM
 */
export function removeGoogleMapsScript(): void {
  const script = document.querySelector('script.googleMapClass')
  if (script) {
    script.remove()
    isScriptLoaded = false
    isScriptLoading = false
    loadPromise = null
  }
}

/**
 * Check if Google Maps API is loaded
 */
export function isGoogleMapsLoaded(): boolean {
  return isScriptLoaded && !!window.google?.maps
}

/**
 * Wait for Google Maps API to be available
 */
export function waitForGoogleMaps(timeout = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isGoogleMapsLoaded()) {
      resolve()
      return
    }

    const startTime = Date.now()
    const checkInterval = setInterval(() => {
      if (isGoogleMapsLoaded()) {
        clearInterval(checkInterval)
        resolve()
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval)
        reject(new Error('Google Maps API load timeout'))
      }
    }, 100)
  })
}

